import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { AdminButton, AdminCard, SectionHeader, StatusPill } from '../../../components/admin/ui';
import NativeMarkdown from '../../../components/NativeMarkdown';
import { T } from '../../../utils/adminDesignTokens';
import type { AdminRoadmapItem } from '../../../services/adminRoadmapService';
import {
  NoticeLine,
  ReadOnlyNote,
  errText,
  fmtDateTime,
  hint,
  input,
  label,
  useAdminRole,
  useBusy,
  type Notice,
} from '../partners/PartnersShared';
import { ProductUpdatesAdmin } from './productUpdatesAdminService';
import {
  LIMITS,
  STATUSES,
  STATUS_LABEL,
  STATUS_TONE,
  actionsFor,
  blogRetryNeeded,
  changedPayload,
  confirmPublishText,
  createPayload,
  editNote,
  emptyForm,
  formFrom,
  moveItem,
  toggleItem,
  validate,
  type ProductUpdate,
  type ProductUpdateForm,
  type ProductUpdateStatus,
} from './productUpdatesLogic';

const layout: React.CSSProperties = { display: 'grid', gridTemplateColumns: 'minmax(260px, 1fr) 2fr', gap: 16, alignItems: 'start' };
const field: React.CSSProperties = { marginBottom: 14 };
const listButton: React.CSSProperties = {
  display: 'block', width: '100%', textAlign: 'left', background: 'transparent', border: 'none',
  padding: '10px 4px', cursor: 'pointer', borderBottom: '1px solid var(--border-subtle, #e5e7eb)',
};
const counter = (n: number, max: number): React.CSSProperties => ({
  ...hint, color: n > max ? 'var(--status-error-text, #991b1b)' : hint.color,
});

type Selection = { kind: 'none' } | { kind: 'new' } | { kind: 'existing'; update: ProductUpdate };

export const AdminProductUpdates: React.FC = () => {
  const { role, writable, loaded } = useAdminRole();
  const [busy, run] = useBusy();
  const [filter, setFilter] = useState<ProductUpdateStatus | ''>('');
  const [updates, setUpdates] = useState<ProductUpdate[] | null>(null);
  const [listError, setListError] = useState<string | null>(null);
  const [selection, setSelection] = useState<Selection>({ kind: 'none' });
  const [form, setForm] = useState<ProductUpdateForm>(emptyForm());
  const [shipped, setShipped] = useState<AdminRoadmapItem[]>([]);
  const [notice, setNotice] = useState<Notice>(null);

  const load = useCallback(async () => {
    setListError(null);
    try {
      setUpdates(await ProductUpdatesAdmin.list(filter));
    } catch (err) {
      setListError(errText(err));
    }
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    let live = true;
    ProductUpdatesAdmin.shippedRoadmapItems()
      .then((items) => { if (live) setShipped(items); })
      .catch((err) => { if (live) setNotice({ kind: 'error', text: `Could not load shipped roadmap items: ${errText(err)}` }); });
    return () => { live = false; };
  }, []);

  const current = selection.kind === 'existing' ? selection.update : null;
  const actions = actionsFor(current ? current.status : selection.kind === 'new' ? null : 'archived', writable);
  const problems = useMemo(() => validate(form), [form]);
  const dirty = current ? Object.keys(changedPayload(form, current)).length > 0 : selection.kind === 'new';

  const open = (update: ProductUpdate) => {
    setSelection({ kind: 'existing', update });
    setForm(formFrom(update));
    setNotice(null);
  };

  const startNew = () => {
    setSelection({ kind: 'new' });
    setForm(emptyForm());
    setNotice(null);
  };

  const landed = async (update: ProductUpdate, text: string) => {
    open(update);
    setNotice({ kind: 'ok', text });
    await load();
  };

  const save = () => run(async () => {
    try {
      if (current) {
        await landed(await ProductUpdatesAdmin.edit(current.id, changedPayload(form, current)), 'Saved.');
      } else {
        await landed(await ProductUpdatesAdmin.create(createPayload(form)), 'Draft created.');
      }
    } catch (err) {
      setNotice({ kind: 'error', text: errText(err) });
    }
  });

  const transition = (name: 'approve' | 'publish' | 'archive' | 'postToBlog', done: string) => run(async () => {
    if (!current) return;
    try {
      await landed(await ProductUpdatesAdmin[name](current.id), done);
    } catch (err) {
      const text = errText(err);
      try {
        open(await ProductUpdatesAdmin.get(current.id));
        await load();
        setNotice({ kind: 'error', text });
      } catch (refreshErr) {
        setNotice({ kind: 'error', text: `${text}\nThe page could not reload this update: ${errText(refreshErr)}` });
      }
    }
  });

  const publish = () => {
    if (window.confirm(confirmPublishText(form))) transition('publish', 'Published. Athletes can read it now.');
  };

  const archive = () => {
    if (window.confirm('Archive this update? It leaves the app and cannot be edited again.')) {
      transition('archive', 'Archived.');
    }
  };

  const uploadHero = (file: File | undefined) => {
    if (!file) return;
    run(async () => {
      try {
        const url = await ProductUpdatesAdmin.uploadHero(file);
        setForm((f) => ({ ...f, hero_image_url: url }));
      } catch (err) {
        setNotice({ kind: 'error', text: `The image did not upload: ${errText(err)}` });
      }
    });
  };

  const set = (key: keyof ProductUpdateForm) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
    setForm((f) => ({ ...f, [key]: e.target.value }));

  const shippedById = useMemo(() => new Map(shipped.map((i) => [i.id, i])), [shipped]);
  const linkedTitle = (id: string) =>
    shippedById.get(id)?.title ?? current?.roadmap_items.find((i) => i.id === id)?.title ?? id;
  const editable = actions.canEdit;

  return (
    <div style={{ padding: 24 }}>
      <SectionHeader title="Product Updates" subtitle="Stories about what shipped. Write, approve, then publish to every athlete." />
      <ReadOnlyNote writable={writable} loaded={loaded} role={role} subject="product updates" />
      <NoticeLine notice={notice} onClose={() => setNotice(null)} />
      <div style={layout}>
        <AdminCard>
          <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
            <select aria-label="Status filter" value={filter} onChange={(e) => setFilter(e.target.value as ProductUpdateStatus | '')} style={input}>
              <option value="">All</option>
              {STATUSES.map((s) => <option key={s} value={s}>{STATUS_LABEL[s]}</option>)}
            </select>
            {writable && <AdminButton size="sm" onClick={startNew}>New update</AdminButton>}
          </div>
          {listError && <NoticeLine notice={{ kind: 'error', text: `Could not load updates: ${listError}` }} />}
          {listError && <AdminButton size="sm" variant="secondary" onClick={load}>Retry</AdminButton>}
          {!listError && updates === null && <p style={hint}>Loading…</p>}
          {updates && updates.length === 0 && <p style={hint}>No updates yet.</p>}
          {updates?.map((u) => (
            <button key={u.id} type="button" style={listButton} onClick={() => open(u)} aria-label={`Open ${u.title}`}>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                <strong>{u.title}</strong>
                <StatusPill status={STATUS_TONE[u.status]}>{STATUS_LABEL[u.status]}</StatusPill>
              </div>
              <div style={hint}>{u.published_at ? `Published ${fmtDateTime(u.published_at)}` : `Edited ${fmtDateTime(u.updated_at)}`}</div>
            </button>
          ))}
        </AdminCard>

        {selection.kind === 'none' ? (
          <AdminCard><p style={hint}>Pick an update, or start a new one.</p></AdminCard>
        ) : (
          <AdminCard>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <strong>{current ? current.title : 'New update'}</strong>
              {current && <StatusPill status={STATUS_TONE[current.status]}>{STATUS_LABEL[current.status]}</StatusPill>}
            </div>
            {editNote(current?.status ?? null) && <NoticeLine notice={{ kind: 'info', text: editNote(current?.status ?? null) as string }} />}

            <div style={field}>
              <label style={label} htmlFor="pu-title">Title</label>
              <input id="pu-title" style={{ ...input, width: '100%' }} value={form.title} onChange={set('title')} disabled={!editable} />
              <div style={counter(form.title.length, LIMITS.title)}>{form.title.length}/{LIMITS.title}</div>
            </div>
            <div style={field}>
              <label style={label} htmlFor="pu-summary">Summary (the line under the title in the list)</label>
              <textarea id="pu-summary" style={{ ...input, width: '100%', minHeight: 56 }} value={form.summary} onChange={set('summary')} disabled={!editable} />
              <div style={counter(form.summary.length, LIMITS.summary)}>{form.summary.length}/{LIMITS.summary}</div>
            </div>
            <div style={field}>
              <label style={label} htmlFor="pu-body">Story (markdown)</label>
              <textarea id="pu-body" style={{ ...input, width: '100%', minHeight: 220, fontFamily: 'monospace' }} value={form.body_markdown} onChange={set('body_markdown')} disabled={!editable} />
              <div style={counter(form.body_markdown.length, LIMITS.body_markdown)}>{form.body_markdown.length}/{LIMITS.body_markdown}</div>
            </div>
            <div style={field}>
              <span style={label}>Preview (as the app draws it)</span>
              <div aria-label="Story preview" style={{ padding: 12, borderRadius: 10, background: 'var(--surface-subtle, #f9fafb)' }}>
                <NativeMarkdown text={form.body_markdown} textStyle={{ color: T.textPrimary, fontSize: 15, lineHeight: 22 }} />
              </div>
            </div>
            <div style={field}>
              <label style={label} htmlFor="pu-hero">Hero image</label>
              <input id="pu-hero" style={{ ...input, width: '100%' }} value={form.hero_image_url} onChange={set('hero_image_url')} placeholder="https://…" disabled={!editable} />
              {editable && <input type="file" accept="image/*" aria-label="Upload hero image" onChange={(e) => uploadHero(e.target.files?.[0])} />}
            </div>
            <div style={field}>
              <label style={label} htmlFor="pu-push-title">Notification title</label>
              <input id="pu-push-title" style={{ ...input, width: '100%' }} value={form.push_title} onChange={set('push_title')} disabled={!editable} />
              <div style={counter(form.push_title.length, LIMITS.push_title)}>{form.push_title.length}/{LIMITS.push_title}</div>
            </div>
            <div style={field}>
              <label style={label} htmlFor="pu-push-body">Notification text</label>
              <textarea id="pu-push-body" style={{ ...input, width: '100%', minHeight: 56 }} value={form.push_body} onChange={set('push_body')} disabled={!editable} />
              <div style={counter(form.push_body.length, LIMITS.push_body)}>{form.push_body.length}/{LIMITS.push_body}</div>
            </div>

            <div style={field}>
              <label style={{ ...label, display: 'flex', gap: 8, alignItems: 'center' }} htmlFor="pu-blog">
                <input
                  id="pu-blog"
                  type="checkbox"
                  checked={form.post_to_blog}
                  onChange={(e) => setForm((f) => ({ ...f, post_to_blog: e.target.checked }))}
                  disabled={!editable || current?.status === 'published'}
                />
                Also post to the website blog when published
              </label>
            </div>

            <div style={field}>
              <span style={label}>Roadmap items this story covers</span>
              {form.roadmap_item_ids.map((id, i) => (
                <div key={id} style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 4 }}>
                  <span style={{ flex: 1 }}>{linkedTitle(id)}</span>
                  {editable && (
                    <>
                      <AdminButton size="sm" variant="ghost" aria-label={`Move ${linkedTitle(id)} up`} disabled={i === 0} onClick={() => setForm((f) => ({ ...f, roadmap_item_ids: moveItem(f.roadmap_item_ids, id, -1) }))}>↑</AdminButton>
                      <AdminButton size="sm" variant="ghost" aria-label={`Move ${linkedTitle(id)} down`} disabled={i === form.roadmap_item_ids.length - 1} onClick={() => setForm((f) => ({ ...f, roadmap_item_ids: moveItem(f.roadmap_item_ids, id, 1) }))}>↓</AdminButton>
                      <AdminButton size="sm" variant="ghost" aria-label={`Remove ${linkedTitle(id)}`} onClick={() => setForm((f) => ({ ...f, roadmap_item_ids: toggleItem(f.roadmap_item_ids, id) }))}>Remove</AdminButton>
                    </>
                  )}
                </div>
              ))}
              {editable && (
                <select
                  aria-label="Add a shipped roadmap item"
                  style={input}
                  value=""
                  onChange={(e) => { const id = e.target.value; if (id) setForm((f) => ({ ...f, roadmap_item_ids: toggleItem(f.roadmap_item_ids, id) })); }}
                >
                  <option value="">Add a shipped item…</option>
                  {shipped.filter((i) => !form.roadmap_item_ids.includes(i.id)).map((i) => <option key={i.id} value={i.id}>{i.title}</option>)}
                </select>
              )}
            </div>

            {problems.length > 0 && editable && <NoticeLine notice={{ kind: 'error', text: problems.join('\n') }} />}
            {current?.approved_at && <p style={hint}>Approved {fmtDateTime(current.approved_at)}</p>}
            {current?.published_at && <p style={hint}>Published {fmtDateTime(current.published_at)}</p>}
            {current?.blog_post_id && <p style={hint}>Posted to the website blog.</p>}

            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 8 }}>
              {editable && <AdminButton onClick={save} disabled={busy || problems.length > 0 || !dirty}>{current ? 'Save' : 'Create draft'}</AdminButton>}
              {actions.canApprove && <AdminButton variant="secondary" onClick={() => transition('approve', 'Approved. Publish when ready.')} disabled={busy || dirty}>Approve</AdminButton>}
              {actions.canPublish && <AdminButton onClick={publish} disabled={busy || dirty}>Publish</AdminButton>}
              {blogRetryNeeded(current, writable) && <AdminButton variant="secondary" onClick={() => transition('postToBlog', 'Posted to the website blog.')} disabled={busy}>Post to blog</AdminButton>}
              {actions.canArchive && <AdminButton variant="danger" onClick={archive} disabled={busy}>Archive</AdminButton>}
            </div>
            {dirty && current && (actions.canApprove || actions.canPublish) && <p style={hint}>Save your changes before approving or publishing.</p>}
          </AdminCard>
        )}
      </div>
    </div>
  );
};

export default AdminProductUpdates;
